/**
 * Onboarding Step 2: Company Details
 *
 * Collects: company_name, legal_form, legal_form_other, industry_sector,
 * nip, declared_certificate (pirx-waua), declared_certificate_other
 */

import { type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  DECLARED_CERTIFICATE_OTHER_CODE,
  DECLARED_CERTIFICATE_OPTIONS,
  declaredCertificateTestSlug,
  type IndustrySectorCode,
  INDUSTRY_SECTOR_OTHER_CODE,
  INDUSTRY_SECTOR_OPTIONS,
} from "@/constants/company-form-options";
import { useLegalFormOptions } from "@/features/onboarding/hooks/useLegalFormOptions";

interface FormData {
  company_name: string;
  legal_form: string;
  legal_form_other: string;
  industry_sector: string[];
  industry_sector_other: string;
  nip: string;
  declared_certificate: string[];
  declared_certificate_other: string;
}
interface Step2DetailsProps {
  values: FormData;
  onChange: (
    field: keyof FormData,
    value: string | string[],
  ) => void;
  errors?: Partial<Record<keyof FormData, string>>;
}

const fieldClassName =
  "mt-2 w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-4 py-3 text-sm text-[var(--foreground)] shadow-sm transition focus:border-[var(--ring)] focus:outline-none focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]";

export function Step2Details(
  { values, onChange, errors = {} }: Step2DetailsProps,
) {
  const { t } = useTranslation(["onboarding", "validation"]);
  const { options: legalFormOptions } = useLegalFormOptions();

  const handleInputChange = (
    field: Exclude<keyof FormData, "industry_sector" | "declared_certificate">,
  ) =>
  (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    onChange(field, e.target.value);
  };

  const handleLegalFormChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onChange("legal_form", value);
    if (value !== "other") {
      onChange("legal_form_other", "");
    }
  };

  // pirx-lus53: toggle a certificate in the multi-select; when "other" is
  // deselected, drop the accompanying free-text.
  const toggleDeclaredCertificate = (value: string) => {
    const current = values.declared_certificate;
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];

    onChange("declared_certificate", next);

    if (!next.includes(DECLARED_CERTIFICATE_OTHER_CODE)) {
      onChange("declared_certificate_other", "");
    }
  };
  const toggleIndustrySector = (value: IndustrySectorCode) => {
    const currentlySelected = values.industry_sector;
    const isSelected = currentlySelected.includes(value);
    const nextSelection = isSelected
      ? currentlySelected.filter((item: string) => item !== value)
      : [...currentlySelected, value];

    onChange("industry_sector", nextSelection);

    if (!nextSelection.includes(INDUSTRY_SECTOR_OTHER_CODE)) {
      onChange("industry_sector_other", "");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label
          htmlFor="company-name"
          className="mb-2 block text-sm font-semibold text-[var(--foreground)]"
        >
          {t("onboarding:step2.companyName.label")}
        </label>
        <input
          id="company-name"
          type="text"
          value={values.company_name}
          onChange={handleInputChange("company_name")}
          className={`${fieldClassName} ${
            errors.company_name
              ? "border-[color:color-mix(in_srgb,var(--destructive)_35%,transparent)] focus:border-[var(--destructive)] focus:ring-[color:color-mix(in_srgb,var(--destructive)_20%,transparent)]"
              : ""
          }`}
          placeholder={t("onboarding:step2.companyName.placeholder")}
          aria-invalid={errors.company_name ? true : undefined}
          aria-describedby={errors.company_name ? "company-name-error" : undefined}
        />
        {errors.company_name && (
          <p id="company-name-error" role="alert" className="mt-2 text-xs font-medium text-[var(--destructive)]">{errors.company_name}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="legal-form"
          className="mb-2 block text-sm font-semibold text-[var(--foreground)]"
        >
          {t("onboarding:step2.legalForm.label")}
        </label>
        <select
          id="legal-form"
          value={values.legal_form}
          onChange={handleLegalFormChange}
          className={`${fieldClassName} ${
            errors.legal_form
              ? "border-[color:color-mix(in_srgb,var(--destructive)_35%,transparent)] focus:border-[var(--destructive)] focus:ring-[color:color-mix(in_srgb,var(--destructive)_20%,transparent)]"
              : ""
          }`}
          aria-label={t("onboarding:step2.legalForm.label")}
          aria-invalid={errors.legal_form ? true : undefined}
          aria-describedby={errors.legal_form ? "legal-form-error" : undefined}
        >
          {legalFormOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.legal_form && (
          <p id="legal-form-error" role="alert" className="mt-2 text-xs font-medium text-[var(--destructive)]">{errors.legal_form}</p>
        )}

        {values.legal_form === "other" && (
          <input
            id="legal-form-other"
            type="text"
            value={values.legal_form_other}
            onChange={handleInputChange("legal_form_other")}
            className={`${fieldClassName} mt-3 ${
              errors.legal_form_other
                ? "border-[color:color-mix(in_srgb,var(--destructive)_35%,transparent)] focus:border-[var(--destructive)] focus:ring-[color:color-mix(in_srgb,var(--destructive)_20%,transparent)]"
                : ""
            }`}
            placeholder={t("onboarding:step2.legalForm.otherPlaceholder")}
            aria-label={t("onboarding:step2.legalForm.otherPlaceholder")}
            aria-invalid={errors.legal_form_other ? true : undefined}
            aria-describedby={errors.legal_form_other ? "legal-form-other-error" : undefined}
          />
        )}
        {errors.legal_form_other && (
          <p id="legal-form-other-error" role="alert" className="mt-2 text-xs font-medium text-[var(--destructive)]">
            {errors.legal_form_other}
          </p>
        )}
      </div>

      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 block p-0 text-sm font-semibold text-[var(--foreground)]">
          {t("onboarding:step2.industrySector.label")}
        </legend>
        <p className="mb-3 text-sm leading-6 text-[var(--muted-foreground)]">
          {t("onboarding:step2.industrySector.hint")}
        </p>
        <div
          id="industry-sector-group"
          tabIndex={-1}
          className="max-h-60 overflow-y-auto rounded-[var(--r-lg)] border border-[var(--border)] bg-[color:color-mix(in_srgb,var(--background)_88%,transparent)] p-4 focus:outline-none focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]"
          aria-invalid={errors.industry_sector ? true : undefined}
          aria-describedby={errors.industry_sector ? "industry-sector-error" : undefined}
        >
          <div className="space-y-2">
            {INDUSTRY_SECTOR_OPTIONS.map((option) => {
              const checked = values.industry_sector.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-[var(--r-md)] border px-3 py-3 text-sm transition ${
                    checked
                      ? "border-[color:color-mix(in_srgb,var(--primary)_26%,transparent)] bg-[color:color-mix(in_srgb,var(--primary)_10%,var(--card))] text-[var(--foreground)]"
                      : "border-transparent bg-[var(--card)] text-[var(--muted-foreground)] hover:border-[var(--border)] hover:text-[var(--foreground)]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleIndustrySector(option.value)}
                    className="mt-0.5 rounded border-[var(--input)] text-[var(--primary)] focus:ring-[var(--ring)]"
                    data-test={`industry-sector-${option.value.toLowerCase()}`}
                  />
                  <span>{t(option.labelKey)}</span>
                </label>
              );
            })}
          </div>
        </div>
        {errors.industry_sector && (
          <p id="industry-sector-error" role="alert" className="mt-2 text-xs font-medium text-[var(--destructive)]">{errors.industry_sector}</p>
        )}

        {values.industry_sector.includes(INDUSTRY_SECTOR_OTHER_CODE) && (
          <input
            id="industry-sector-other"
            type="text"
            value={values.industry_sector_other}
            onChange={handleInputChange("industry_sector_other")}
            className={`${fieldClassName} mt-3 ${
              errors.industry_sector_other
                ? "border-[color:color-mix(in_srgb,var(--destructive)_35%,transparent)] focus:border-[var(--destructive)] focus:ring-[color:color-mix(in_srgb,var(--destructive)_20%,transparent)]"
                : ""
            }`}
            placeholder={t("onboarding:step2.industrySector.otherPlaceholder")}
            aria-label={t("onboarding:step2.industrySector.otherPlaceholder")}
            aria-invalid={errors.industry_sector_other ? true : undefined}
            aria-describedby={errors.industry_sector_other ? "industry-sector-other-error" : undefined}
          />
        )}
        {errors.industry_sector_other && (
          <p id="industry-sector-other-error" role="alert" className="mt-2 text-xs font-medium text-[var(--destructive)]">
            {errors.industry_sector_other}
          </p>
        )}
      </fieldset>

      <div>
        <label
          htmlFor="company-nip"
          className="mb-2 block text-sm font-semibold text-[var(--foreground)]"
        >
          {t("onboarding:step2.nip.label")}
        </label>
        <input
          id="company-nip"
          type="text"
          inputMode="numeric"
          value={values.nip}
          onChange={handleInputChange("nip")}
          className={`${fieldClassName} ${
            errors.nip
              ? "border-[color:color-mix(in_srgb,var(--destructive)_35%,transparent)] focus:border-[var(--destructive)] focus:ring-[color:color-mix(in_srgb,var(--destructive)_20%,transparent)]"
              : ""
          }`}
          placeholder={t("onboarding:step2.nip.placeholder")}
          aria-label={t("onboarding:step2.nip.label")}
          aria-invalid={errors.nip ? true : undefined}
          aria-describedby={errors.nip ? "company-nip-error" : undefined}
        />
        {errors.nip && (
          <p id="company-nip-error" role="alert" className="mt-2 text-xs font-medium text-[var(--destructive)]">{errors.nip}</p>
        )}
      </div>

      {/* pirx-lus53: optional declared-certificate multi-select + 'other' free-text */}
      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 block p-0 text-sm font-semibold text-[var(--foreground)]">
          {t("onboarding:step2.declaredCertificate.label")}
        </legend>
        <p className="mb-3 text-sm leading-6 text-[var(--muted-foreground)]">
          {t("onboarding:step2.declaredCertificate.hint")}
        </p>
        <div
          className="max-h-60 overflow-y-auto rounded-[var(--r-lg)] border border-[var(--border)] bg-[color:color-mix(in_srgb,var(--background)_88%,transparent)] p-4"
          aria-invalid={errors.declared_certificate ? true : undefined}
          aria-describedby={errors.declared_certificate ? "declared-certificate-error" : undefined}
        >
          <div className="space-y-2">
            {DECLARED_CERTIFICATE_OPTIONS.map((option) => {
              const checked = values.declared_certificate.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-[var(--r-md)] border px-3 py-3 text-sm transition ${
                    checked
                      ? "border-[color:color-mix(in_srgb,var(--primary)_26%,transparent)] bg-[color:color-mix(in_srgb,var(--primary)_10%,var(--card))] text-[var(--foreground)]"
                      : "border-transparent bg-[var(--card)] text-[var(--muted-foreground)] hover:border-[var(--border)] hover:text-[var(--foreground)]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDeclaredCertificate(option.value)}
                    className="mt-0.5 rounded border-[var(--input)] text-[var(--primary)] focus:ring-[var(--ring)]"
                    data-test={`declared-certificate-${declaredCertificateTestSlug(option.value)}`}
                  />
                  <span>{t(option.labelKey)}</span>
                </label>
              );
            })}
          </div>
        </div>
        {errors.declared_certificate && (
          <p id="declared-certificate-error" role="alert" className="mt-2 text-xs font-medium text-[var(--destructive)]">
            {errors.declared_certificate}
          </p>
        )}
        <p className="mt-2 text-xs leading-5 text-[var(--muted-foreground)]">
          {t("onboarding:step2.declaredCertificate.disclaimer")}
        </p>
        {values.declared_certificate.includes(DECLARED_CERTIFICATE_OTHER_CODE) && (
          <input
            id="declared-certificate-other"
            type="text"
            value={values.declared_certificate_other}
            onChange={handleInputChange("declared_certificate_other")}
            className={`${fieldClassName} mt-3 ${
              errors.declared_certificate_other
                ? "border-[color:color-mix(in_srgb,var(--destructive)_35%,transparent)] focus:border-[var(--destructive)] focus:ring-[color:color-mix(in_srgb,var(--destructive)_20%,transparent)]"
                : ""
            }`}
            placeholder={t(
              "onboarding:step2.declaredCertificate.otherPlaceholder",
            )}
            aria-invalid={errors.declared_certificate_other ? true : undefined}
            aria-describedby={errors.declared_certificate_other ? "declared-certificate-other-error" : undefined}
            aria-label={t(
              "onboarding:step2.declaredCertificate.otherPlaceholder",
            )}
            data-test="declared-certificate-other-text"
          />
        )}
        {errors.declared_certificate_other && (
          <p id="declared-certificate-other-error" role="alert" className="mt-2 text-xs font-medium text-[var(--destructive)]">
            {errors.declared_certificate_other}
          </p>
        )}
      </fieldset>
    </div>
  );
}
